import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export const InstallPrompt: React.FC<{ variant?: 'sidebar' | 'toast' }> = ({ variant = 'toast' }) => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Verifica se é iOS
    const isIosDevice = /ipad|iphone|ipod/.test(navigator.userAgent.toLowerCase()) && !(window as any).MSStream;
    // Verifica se já está instalado (standalone)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone;
    
    if (isIosDevice && !isStandalone) {
      setIsIOS(true);
      setIsInstallable(true);
    }

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      alert(t('pwa.ios_alert'));
      return;
    }

    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstallable(false);
    }
    setDeferredPrompt(null);
  };

  if (!isInstallable) return null;

  if (variant === 'sidebar') {
    return (
      <button 
        onClick={handleInstallClick}
        className="hidden sm:flex w-full items-center justify-center gap-2 p-3 mt-4 rounded-lg bg-gradient-to-r from-[#98c136] to-[#7a9a2b] text-[#0a0b0f] font-black uppercase italic tracking-widest text-[10px] hover:scale-[1.02] transition-transform shadow-[0_0_15px_rgba(152,193,54,0.3)] border border-[#98c136]/50"
      >
        <Download size={14} />
        {t('pwa.install_button')}
      </button>
    );
  }

  return (
    <div className="fixed bottom-20 right-3 left-3 sm:left-auto sm:right-6 sm:bottom-6 z-50 p-2.5 sm:p-4 neo-raised bg-[#1a1d26] border-l-4 border-[#98c136] rounded-r-lg sm:rounded-r-xl shadow-2xl max-w-[280px] xs:max-w-xs sm:max-w-sm flex items-center gap-2.5 sm:gap-4 animate-in slide-in-from-right duration-500 fade-in">
      <div className="flex-1 min-w-0">
        <h4 className="font-display font-black italic text-white text-xs sm:text-sm uppercase tracking-wide sm:tracking-wider mb-0.5 sm:mb-1 truncate">e-MSS App</h4>
        <p className="text-console-text-muted text-[11px] sm:text-xs leading-tight sm:leading-relaxed">
          {isIOS 
            ? t('pwa.install_ios') 
            : t('pwa.install_android')}
        </p>
      </div>
      {!isIOS && (
        <button 
          onClick={handleInstallClick}
          className="shrink-0 p-2 sm:p-3 rounded-lg sm:rounded-xl bg-white/5 hover:bg-[#98c136] hover:text-[#0a0b0f] text-[#98c136] transition-all"
          aria-label={t('pwa.install_button')}
        >
          <Download className="w-4 h-4 sm:w-5 sm:h-5" />
        </button>
      )}
    </div>
  );
};
